import { useCallback, useEffect, useRef, useState } from 'react';

import type { UseAutoSaveListDraftParams, UseAutoSaveListDraftResult } from './types';

export default function useAutoSaveListDraft<TRow>({
  entries,
  getRowKey,
  disabled,
  autoOpenWhenEmpty = false,
  addDisabled = false,
  onDelete,
}: UseAutoSaveListDraftParams<TRow>): UseAutoSaveListDraftResult<TRow> {
  const [hasDraft, setHasDraft] = useState(false);
  const [draftSeq, setDraftSeq] = useState(0);
  const [draftValid, setDraftValid] = useState(false);
  const [draftPersisted, setDraftPersisted] = useState(true);
  const [draftCreatedId, setDraftCreatedId] = useState<number | null>(null);
  const [pendingDeleteRow, setPendingDeleteRow] = useState<TRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const draftOpenRef = useRef(false);

  useEffect(() => {
    if (autoOpenWhenEmpty && !hasDraft && !disabled && !addDisabled && entries.length === 0) {
      draftOpenRef.current = true;
      setDraftSeq((seq) => seq + 1);
      setHasDraft(true);
    }
  }, [addDisabled, autoOpenWhenEmpty, disabled, entries.length, hasDraft]);

  useEffect(() => {
    // Keep an in-flight create mounted: draftCreatedId is still null until onCreate settles.
    if (addDisabled && hasDraft && draftCreatedId == null && draftPersisted) {
      draftOpenRef.current = false;
      setHasDraft(false);
    }
  }, [addDisabled, draftCreatedId, draftPersisted, hasDraft]);

  const setDraftCreatedIdIfOpen = useCallback((id: number | null) => {
    if (id != null && !draftOpenRef.current) return;
    setDraftCreatedId(id);
  }, []);

  const savedDraftRow =
    draftCreatedId == null
      ? null
      : (entries.find((row) => getRowKey(row) === draftCreatedId) ?? null);
  const visibleEntries =
    draftCreatedId == null ? entries : entries.filter((row) => getRowKey(row) !== draftCreatedId);

  const openDraft = useCallback(() => {
    draftOpenRef.current = true;
    setDraftCreatedId(null);
    setDraftValid(false);
    setDraftPersisted(true);
    setDraftSeq((seq) => seq + 1);
    setHasDraft(true);
  }, []);

  const commitPlainDraft = useCallback(() => {
    draftOpenRef.current = false;
    setHasDraft(false);
    setDraftValid(false);
    setDraftPersisted(true);
    setDraftCreatedId(null);
  }, []);

  const discardDraft = useCallback(() => {
    draftOpenRef.current = false;
    setHasDraft(false);
    setDraftPersisted(true);
    setDraftCreatedId(null);
  }, []);

  const closeDelete = useCallback(() => setPendingDeleteRow(null), []);

  const confirmDelete = useCallback(async () => {
    if (disabled || isDeleting || pendingDeleteRow == null) return;
    const rowId = getRowKey(pendingDeleteRow);
    setIsDeleting(true);
    try {
      const deleted = await onDelete(rowId);
      if (!deleted) return;
      setPendingDeleteRow(null);
      if (rowId === draftCreatedId) {
        draftOpenRef.current = false;
        setHasDraft(false);
        setDraftCreatedId(null);
      }
    } catch {
      return;
    } finally {
      setIsDeleting(false);
    }
  }, [disabled, draftCreatedId, getRowKey, isDeleting, onDelete, pendingDeleteRow]);

  return {
    hasDraft,
    draftSeq,
    draftValid,
    draftPersisted,
    draftCreatedId,
    setDraftValid,
    setDraftPersisted,
    setDraftCreatedId: setDraftCreatedIdIfOpen,
    savedDraftRow,
    visibleEntries,
    pendingDeleteRow,
    setPendingDeleteRow,
    isDeleting,
    openDraft,
    commitPlainDraft,
    discardDraft,
    confirmDelete,
    closeDelete,
  };
}
